import { useCallback, useEffect, useRef, useState } from 'react'
import { api } from './client'

interface Resource<T> {
  data: T | null
  error: unknown
  loading: boolean
  reload: () => void
}

/**
 * Einfaches Laden einer Ressource. Absichtlich klein gehalten: der Zustand dieser Anwendung
 * ist überschaubar, und ein Abfrage-Cache würde bei Listen mit Cursor eigene Fragen
 * aufwerfen, die hier nichts lösen.
 */
export function useResource<T>(path: string | null, deps: unknown[] = []): Resource<T> {
  const [data, setData] = useState<T | null>(null)
  const [error, setError] = useState<unknown>(null)
  const [loading, setLoading] = useState(path !== null)
  const [nonce, setNonce] = useState(0)
  const latest = useRef(0)

  useEffect(() => {
    if (path === null) {
      setData(null)
      setLoading(false)
      return
    }

    const request = ++latest.current
    setLoading(true)
    setError(null)

    api.get<T>(path)
      .then(result => {
        // Antworten überholter Anfragen verwerfen, sonst blinkt die Liste beim Filtern.
        if (request === latest.current) setData(result)
      })
      .catch(caught => {
        if (request === latest.current) setError(caught)
      })
      .finally(() => {
        if (request === latest.current) setLoading(false)
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, nonce, ...deps])

  return { data, error, loading, reload: () => setNonce(value => value + 1) }
}

interface InfiniteList<T> {
  items: T[]
  error: unknown
  loading: boolean
  hasMore: boolean
  loadMore: () => void
  reload: () => void
}

/** Cursor-Liste mit Nachladen beim Scrollen. */
export function useInfinite<T>(path: string | null, limit = 50): InfiniteList<T> {
  const [items, setItems] = useState<T[]>([])
  const [cursor, setCursor] = useState<string | null>(null)
  const [hasMore, setHasMore] = useState(true)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<unknown>(null)
  const [nonce, setNonce] = useState(0)
  const generation = useRef(0)

  // Bei geändertem Pfad von vorn beginnen: ein Cursor gehört zu genau einem Filter.
  useEffect(() => {
    generation.current++
    setItems([])
    setCursor(null)
    setHasMore(path !== null)
    setError(null)
  }, [path, nonce])

  const load = useCallback(async (nextCursor: string | null, forGeneration: number) => {
    if (path === null) return
    setLoading(true)
    try {
      const page = await api.page<T>(path, nextCursor, limit)
      if (forGeneration !== generation.current) return
      setItems(current => (nextCursor === null ? page.items : [...current, ...page.items]))
      setCursor(page.nextCursor)
      setHasMore(page.nextCursor !== null)
    } catch (caught) {
      if (forGeneration === generation.current) {
        setError(caught)
        setHasMore(false)
      }
    } finally {
      if (forGeneration === generation.current) setLoading(false)
    }
  }, [path, limit])

  useEffect(() => {
    if (path === null) return
    void load(null, generation.current)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, nonce])

  const loadMore = useCallback(() => {
    if (!loading && hasMore && cursor !== null) void load(cursor, generation.current)
  }, [loading, hasMore, cursor, load])

  return {
    items, error, loading, hasMore,
    loadMore,
    reload: () => setNonce(value => value + 1),
  }
}

/** Kurzmeldungen unten rechts. */
export function useToast() {
  const [toast, setToast] = useState<{ message: string; kind: 'ok' | 'warn' } | null>(null)
  return {
    toast,
    show: (message: string, kind: 'ok' | 'warn' = 'ok') => setToast({ message, kind }),
    clear: () => setToast(null),
  }
}
